import { ColumnData, DisplayRow, Filters, isRangeFilter, RowBase, RowEdits, Sorting } from './priceGridTypes';

export function parsePriceEditValue(value: string) {
  return Number(value);
}

export function getRowsScopeKey<TRow extends RowBase>(scope: string, rows: readonly TRow[]) {
  return `${scope}:${rows
    .map((row) => row.guid)
    .toSorted()
    .join(',')}`;
}

export function filterAndSortRows<TRow extends RowBase>(
  rows: readonly TRow[],
  columns: readonly ColumnData<TRow>[],
  filters: Filters,
  sortings: readonly Sorting[],
): readonly TRow[] {
  let filteredRows = rows;
  for (const column of columns) {
    const filterForColumn = filters[column.key];
    if (Array.isArray(filterForColumn) && filterForColumn.length > 0) {
      filteredRows = filteredRows.filter((row) => filterForColumn.includes(column.getCellValue(row)));
    } else if (typeof filterForColumn === 'string' && filterForColumn) {
      filteredRows = filteredRows.filter((row) =>
        column.getCellValue(row).toLowerCase().includes(filterForColumn.toLowerCase()),
      );
    } else if (isRangeFilter(filterForColumn)) {
      const min = filterForColumn.min === '' ? undefined : Number(filterForColumn.min);
      const max = filterForColumn.max === '' ? undefined : Number(filterForColumn.max);
      const getNumericValue = column.sortByMapping;
      if (!getNumericValue) continue;

      filteredRows = filteredRows.filter((row) => {
        const value = getNumericValue(row);
        if (typeof value !== 'number') return false;
        return (min == null || Number.isNaN(min) || value >= min) && (max == null || Number.isNaN(max) || value <= max);
      });
    }
  }

  return filteredRows.toSorted((a, b) => {
    for (const sorting of sortings) {
      const column = columns.find((column) => column.key === sorting.columnKey)!;
      const sortByMapping = column.sortByMapping ?? column.getCellValue;
      const aValue = sortByMapping(a);
      const bValue = sortByMapping(b);

      let ascResult;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        ascResult = column.compare ? column.compare(aValue, bValue) : aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        ascResult = aValue - bValue;
      } else {
        console.error(`Unrecognized values for sorting: ${aValue} and ${bValue}`);
        continue;
      }

      if (ascResult === 0) continue;

      return ascResult * (sorting.direction === 'desc' ? -1 : 1);
    }

    return a.guid.localeCompare(b.guid);
  });
}

export function buildDisplayRows<TRow extends RowBase>(
  filteredAndSortedRows: readonly TRow[],
  columns: readonly ColumnData<TRow>[],
  groupByColumnKey: string,
  expandedGroupKeys: readonly string[],
): readonly DisplayRow<TRow>[] {
  if (!groupByColumnKey) {
    return filteredAndSortedRows.map((row) => ({ type: 'price', row }));
  }

  const groupByColumn = columns.find((column) => column.key === groupByColumnKey)!;
  const rowsByGroup = new Map<string, { label: string; rows: TRow[] }>();

  for (const row of filteredAndSortedRows) {
    const value = groupByColumn.getGroupValue ? groupByColumn.getGroupValue(row) : groupByColumn.getCellValue(row);
    // Group keys scope bulk edit drafts and expansion state, so they must include the column:
    // otherwise a draft typed under one grouping resurfaces under another grouping whenever the
    // two groupings produce the same value.
    const groupKey = `${groupByColumnKey}:${value}`;
    const group = rowsByGroup.get(groupKey);

    if (group) {
      group.rows.push(row);
    } else {
      rowsByGroup.set(groupKey, { label: `${groupByColumn.title}: ${value}`, rows: [row] });
    }
  }

  return Array.from(rowsByGroup.entries()).flatMap(([key, group]) => [
    { type: 'group' as const, key, label: group.label, rows: group.rows },
    ...(expandedGroupKeys.includes(key) ? group.rows.map((row) => ({ type: 'price' as const, row })) : []),
  ]);
}

export function getValidationErrorsByColumnKey<TRow extends RowBase>(
  editableColumns: readonly ColumnData<TRow>[],
  edits: RowEdits,
): { readonly [columnKey: string]: string | undefined } {
  const validationErrors: { [columnKey: string]: string | undefined } = {};

  for (const column of editableColumns) {
    const editValue = edits[column.key];
    if (editValue != null) validationErrors[column.key] = column.validateEditValue?.(editValue);
  }

  return validationErrors;
}

export function applyEditsToRow<TRow extends RowBase>(
  columns: readonly ColumnData<TRow>[],
  row: TRow,
  edits: RowEdits,
): { readonly editedRow: TRow } | { readonly validationError: string } {
  let editedRow = row;

  for (const column of columns) {
    if (!column.getUpdatedRowWithEdit) continue;

    const newValueForColumn = edits[column.key];
    if (newValueForColumn == null) continue;

    const validationError = column.validateEditValue?.(newValueForColumn);
    if (validationError) return { validationError };

    editedRow = column.getUpdatedRowWithEdit(editedRow, newValueForColumn);
  }

  return { editedRow };
}

export function getUpdatedRows<TRow extends RowBase>(
  columns: readonly ColumnData<TRow>[],
  rowsToEdit: readonly TRow[],
  edits: RowEdits,
): { readonly editedRows: readonly TRow[] } | { readonly validationError: string } {
  const editedRows: TRow[] = [];

  for (const row of rowsToEdit) {
    const result = applyEditsToRow(columns, row, edits);
    if ('validationError' in result) return result;
    if (result.editedRow !== row) editedRows.push(result.editedRow);
  }

  return { editedRows };
}

export function getCommonEditValue<TRow extends RowBase>(column: ColumnData<TRow>, rowsToEdit: readonly TRow[]) {
  if (rowsToEdit.length === 0) return '';

  const getValue = column.getEditValue ?? column.getCellValue;
  const firstValue = getValue(rowsToEdit[0]);
  const allRowsMatch = rowsToEdit.every((row) => getValue(row) === firstValue);

  return allRowsMatch ? firstValue : null;
}
